/**
 * oidc-client-ts UserManager: authorization code + PKCE against VITE_AUTH_AUTHORITY, tokens kept in sessionStorage.
 * The id token (60 min) is renewed with the refresh token before it expires, so the session lasts as long as the
 * refresh token (SPEC decision 43, `sessionRenewal.ts`). The API receives the id token.
 */
import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import { config } from '../config';
import { metadataSeedOf } from './authMetadata';
import { scopesFor } from './passwordModel';
import { liveUser } from './sessionRenewal';

export const REDIRECT_URI = `${window.location.origin}/auth/callback`;
export const POST_LOGOUT_REDIRECT_URI = `${window.location.origin}/`;

let instance: UserManager | null = null;

export function getUserManager(): UserManager {
  if (instance) return instance;
  if (!config.authority || !config.clientId) throw new Error('Auth is not configured (VITE_AUTH_AUTHORITY / VITE_AUTH_CLIENT_ID)');
  const seed = metadataSeedOf(config);
  instance = new UserManager({
    authority: config.authority,
    client_id: config.clientId,
    redirect_uri: REDIRECT_URI,
    post_logout_redirect_uri: POST_LOGOUT_REDIRECT_URI,
    response_type: 'code',
    scope: scopesFor(config.authority),
    // Renews through the refresh token grant (no iframe); a session without a refresh token simply expires.
    automaticSilentRenew: true,
    loadUserInfo: false,
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
    ...(seed ? { metadataSeed: seed } : {}),
  });
  return instance;
}

/** The signed-in user, renewed first when the id token expired; null when there is none or the session ended. */
export async function currentUser(): Promise<User | null> {
  return liveUser(getUserManager());
}

export async function currentIdToken(): Promise<string | null> {
  return (await currentUser())?.id_token ?? null;
}

let dropping = false;
/** Local sign-out used when the API answers 401 or the token expires: forget the user and go to /sign-in. */
export async function dropSession(reason: 'expired' | 'unauthorized' | 'signed-out' = 'expired'): Promise<void> {
  if (dropping) return;
  dropping = true;
  try {
    await getUserManager().removeUser();
  } finally {
    window.location.assign(`/sign-in?reason=${reason}`);
  }
}
